import type { CalendarEvent } from '../../services/api';
import type { ATPPlan, ATPWeek, ATPWeekDetails } from '../../services/atpApi';
import { formatIsoDateLabel } from '../../i18n/formatters';
import i18n from 'i18next';
import { ATP_PHASES, DEFAULT_PHASE_COUNTS, DEFAULT_PHASE_WEEKS } from './constants';

/** atp_priority value for an A-race (TrainingPeaks). */
export const ATP_PRIORITY_A = 1;

/** TrainingPeaks CTL time constant (days). */
const CTL_TIME_CONSTANT = 42;
const CTL_ALPHA = 1 - Math.exp(-1 / CTL_TIME_CONSTANT);

/** Parse week.details whether stored as JSON string or object. */
export function parseWeekDetails(raw: ATPWeek['details']): ATPWeekDetails {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) as ATPWeekDetails;
  } catch {
    return {};
  }
}

export function weekTss(week: ATPWeek): number {
  const details = parseWeekDetails(week.details);
  const fromVol = parseInt(String(week.volume), 10);
  if (!Number.isNaN(fromVol) && fromVol >= 0) return fromVol;
  return details.tss ?? 0;
}

/** Recovery heuristic — matches mobile isRecoveryWeek(). */
export function isRecoveryWeek(phaseKey: string, weekIndex: number, recoveryCycle: 3 | 4 = 4): boolean {
  if (phaseKey === 'taper') return true;
  if (phaseKey === 'peak') return false;
  return (weekIndex + 1) % recoveryCycle === 0;
}

/** Normalise API event_date to YYYY-MM-DD. */
export function eventDateISO(ev: Pick<CalendarEvent, 'event_date'>): string {
  if (!ev.event_date) return todayISO();
  return String(ev.event_date).split(/[T ]/)[0];
}

/** First upcoming A-priority event (sorted list assumed chronological). */
export function getFirstAPriorityEvent(events: CalendarEvent[]): CalendarEvent | null {
  return events.find(ev => ev.atp_priority === ATP_PRIORITY_A) ?? null;
}

/** Whole days from `fromISO` to `toISO` (negative if toISO is earlier). */
export function daysBetweenISO(fromISO: string, toISO: string): number {
  const from = new Date(`${fromISO}T00:00:00`);
  const to = new Date(`${toISO}T00:00:00`);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/** TrainingPeaks daily CTL update for a single day. */
export function applyDailyCtl(ctl: number, dailyTss: number): number {
  return ctl + (dailyTss - ctl) * CTL_ALPHA;
}

/** Ordered weekly TSS values across all phases (optionally scaled). */
export function flattenPhaseWeeklyTss(
  phaseWeekCounts: Record<string, number>,
  weeklyTSS: Record<string, number[]>,
  scale = 1,
): number[] {
  const weeks: number[] = [];
  for (const ph of ATP_PHASES) {
    const count = phaseWeekCounts[ph.key] ?? 0;
    const arr = (weeklyTSS[ph.key] ?? []).slice(0, count);
    for (const tss of arr) {
      weeks.push(Math.max(0, Math.round(tss * scale)));
    }
  }
  return weeks;
}

/**
 * Project CTL after executing a week-by-week TSS plan (42-day CTL model).
 */
export function projectCtlFromWeeklySeries(ctlStart: number, weeklySeries: number[]): number {
  let ctl = ctlStart;
  for (const weekTss of weeklySeries) {
    const daily = weekTss / 7;
    for (let d = 0; d < 7; d++) {
      ctl = applyDailyCtl(ctl, daily);
    }
  }
  return Math.round(ctl);
}

/** Scale every week, preserving zero-load recovery/rest weeks. */
export function scalePhaseWeeklyTss(
  phaseWeekCounts: Record<string, number>,
  weeklyTSS: Record<string, number[]>,
  factor: number,
): Record<string, number[]> {
  const next: Record<string, number[]> = {};
  for (const ph of ATP_PHASES) {
    const count = phaseWeekCounts[ph.key] ?? 0;
    const arr = (weeklyTSS[ph.key] ?? []).slice(0, count);
    next[ph.key] = arr.map(t => Math.max(0, Math.round(t * factor)));
  }
  return resizeWeeklyTssForPhaseCounts(phaseWeekCounts, next);
}

/** Simulate constant weekly TSS for N weeks (daily TSS spread evenly). */
export function simulateCtlWeeks(ctlStart: number, weeks: number, weeklyTss: number): number {
  let ctl = ctlStart;
  const dailyTss = weeklyTss / 7;
  const totalDays = Math.max(0, weeks) * 7;
  for (let d = 0; d < totalDays; d++) {
    ctl = applyDailyCtl(ctl, dailyTss);
  }
  return ctl;
}

/**
 * Back-calculate average weekly TSS needed to reach `ctlTarget` in `weeks` weeks
 * using the same exponential CTL model as the VO2 backend (42-day constant).
 */
export function solveWeeklyTssForCtlTarget(
  ctlStart: number,
  ctlTarget: number,
  weeks: number,
): number {
  if (weeks <= 0) return 0;
  const retention = Math.exp(-(weeks * 7) / CTL_TIME_CONSTANT);
  return Math.max(0, Math.round(7 * (ctlTarget - ctlStart * retention) / (1 - retention)));
}

export interface ARaceCtlMetrics {
  aRaceDateISO: string;
  rampStartISO: string;
  weeksToARace: number;
  ctlRampPerWeek: number;
  estimatedWeeklyTss: number;
}

/**
 * Full plan length in weeks between two ISO dates (training-model phase target).
 * Uses plan start as the anchor (not today), so editing season start/end in step 1
 * updates the phase week target.
 */
export function computePlanWeeksSpan(planStartISO: string, endDateISO: string): number {
  const days = daysBetweenISO(planStartISO, endDateISO);
  return Math.max(1, Math.ceil(Math.max(days + 1, 1) / 7));
}

/** The active training cycle ends at the selected A event. */
export function resolveRequiredPlanWeeks(
  planStartISO: string,
  seasonEndISO: string | null | undefined,
  aRaceDateISO: string | null | undefined,
): number | null {
  const seasonWeeks =
    seasonEndISO && seasonEndISO.length >= 10
      ? computePlanWeeksSpan(planStartISO, seasonEndISO)
      : null;
  const aRaceWeeks =
    aRaceDateISO && aRaceDateISO.length >= 10
      ? computePlanWeeksSpan(planStartISO, aRaceDateISO)
      : null;
  return aRaceWeeks ?? seasonWeeks;
}

/**
 * CTL ramp + weekly TSS projection to the first A-race.
 * Ramp weeks = plan start → start of A-race day, including partial weeks.
 */
export function computeARaceCtlMetrics(params: {
  ctlBase: number;
  ctlTarget: number;
  planStartISO: string;
  aRaceDateISO: string;
}): ARaceCtlMetrics {
  const rampStartISO = params.planStartISO;
  const days = daysBetweenISO(rampStartISO, params.aRaceDateISO);
  const weeksToARace = Math.max(0, days / 7);
  const ctlRampPerWeek = weeksToARace > 0 ? (params.ctlTarget - params.ctlBase) / weeksToARace : 0;
  const estimatedWeeklyTss = solveWeeklyTssForCtlTarget(
    params.ctlBase,
    params.ctlTarget,
    weeksToARace,
  );

  return {
    aRaceDateISO: params.aRaceDateISO,
    rampStartISO,
    weeksToARace,
    ctlRampPerWeek,
    estimatedWeeklyTss,
  };
}

export function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return todayISOFromDate(d);
}

function todayISOFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDateShort(iso: string): string {
  try {
    return formatIsoDateLabel(iso, i18n.language);
  } catch {
    return '—';
  }
}

function validAtpEpochISO(raw: number): string | null {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;

  const millis = value > 10_000_000_000 ? value : value * 1000;
  const date = new Date(millis);
  const year = date.getUTCFullYear();
  if (Number.isNaN(date.getTime()) || year < 2000 || year > 2100) return null;
  // ATP epochs are Laravel calendar dates in config/app.php's Asia/Tehran timezone.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tehran', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function validWeekStartISO(week?: ATPWeek): string | null {
  const value = week?.start_day?.trim().slice(0, 10);
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const year = Number(value.slice(0, 4));
  return year >= 2000 && year <= 2100 ? value : null;
}

export function planStartISO(atp: ATPPlan, weeks: ATPWeek[] = []): string {
  return validWeekStartISO(weeks[0]) ?? validAtpEpochISO(atp.start_week) ?? todayISO();
}

export function planEndISO(atp: ATPPlan, weeks: ATPWeek[] = []): string {
  const persisted = validAtpEpochISO(atp.end_week);
  if (persisted) return persisted;

  const lastWeekStart = validWeekStartISO(weeks[weeks.length - 1]);
  if (lastWeekStart) return addDaysISO(lastWeekStart, 6);

  return addDaysISO(planStartISO(atp, weeks), 7);
}

export function ctlTargetFromPlan(atp: ATPPlan): number {
  return atp.ctl_constant ?? atp.ctl_start ?? 110;
}

/** Rebuild wizard phase state from persisted weeks. */
export function derivePhaseStateFromWeeks(weeks: ATPWeek[]): {
  phaseWeekCounts: Record<string, number>;
  weeklyTSS: Record<string, number[]>;
} {
  const phaseWeekCounts: Record<string, number> = { ...DEFAULT_PHASE_COUNTS };
  const weeklyTSS: Record<string, number[]> = {};

  for (const ph of ATP_PHASES) {
    weeklyTSS[ph.key] = [];
  }

  for (const w of [...weeks].sort((a, b) => a.start_day.localeCompare(b.start_day))) {
    const details = parseWeekDetails(w.details);
    const key = details.phase_key ?? 'base';
    if (!weeklyTSS[key]) weeklyTSS[key] = [];
    weeklyTSS[key].push(weekTss(w));
  }

  for (const ph of ATP_PHASES) {
    const len = weeklyTSS[ph.key]?.length ?? 0;
    phaseWeekCounts[ph.key] = len;
  }

  return { phaseWeekCounts, weeklyTSS };
}

export function buildPhasesPayload(
  phaseWeekCounts: Record<string, number>,
  weeklyTSS: Record<string, number[]>,
) {
  return ATP_PHASES.filter(ph => (phaseWeekCounts[ph.key] ?? 0) > 0).map(ph => ({
    key: ph.key,
    label: ph.label,
    color: ph.color,
    weeks: (weeklyTSS[ph.key] ?? DEFAULT_PHASE_WEEKS[ph.key] ?? []).slice(
      0,
      phaseWeekCounts[ph.key] ?? 0,
    ),
  }));
}

export function computeTotals(
  phaseWeekCounts: Record<string, number>,
  weeklyTSS: Record<string, number[]>,
) {
  const allTSS = ATP_PHASES.flatMap(ph =>
    (weeklyTSS[ph.key] ?? []).slice(0, phaseWeekCounts[ph.key] ?? 0),
  );
  const totalWeeks = Object.values(phaseWeekCounts).reduce((a, b) => a + b, 0);
  const totalTSS = allTSS.reduce((a, b) => a + b, 0);
  const avgTSS = allTSS.length ? Math.round(totalTSS / allTSS.length) : 0;
  return { totalWeeks, totalTSS, avgTSS, allTSS };
}

/**
 * Split `totalWeeks` across ATP phases using DEFAULT_PHASE_COUNTS ratios.
 * Each phase gets at least 1 week; the sum is always exactly `totalWeeks`.
 */
export function distributePhaseWeekCounts(
  totalWeeks: number,
  template: Record<string, number> = DEFAULT_PHASE_COUNTS,
): Record<string, number> {
  const keys = ATP_PHASES.map(p => p.key);
  const counts = Object.fromEntries(keys.map(k => [k, 1])) as Record<string, number>;

  if (totalWeeks <= keys.length) {
    const shortKeys = totalWeeks <= 1 ? ['taper'] : totalWeeks === 2 ? ['b2', 'taper'] : totalWeeks === 3 ? ['b1', 'b2', 'taper'] : totalWeeks === 4 ? ['base', 'b1', 'b2', 'taper'] : keys;
    return Object.fromEntries(keys.map(key => [key, totalWeeks > 0 && shortKeys.includes(key) ? 1 : 0]));
  }

  const templateTotal = keys.reduce((sum, k) => sum + (template[k] ?? 0), 0) || 1;
  const extra = totalWeeks - keys.length;

  const fractional = keys.map(key => {
    const share = (template[key] ?? 0) / templateTotal;
    const exact = share * extra;
    const floor = Math.floor(exact);
    return { key, floor, rem: exact - floor };
  });

  for (const { key, floor } of fractional) {
    counts[key] = 1 + floor;
  }

  let assigned = keys.reduce((sum, k) => sum + counts[k], 0);
  fractional.sort((a, b) => b.rem - a.rem);
  let i = 0;
  while (assigned < totalWeeks) {
    counts[fractional[i % fractional.length].key]++;
    assigned++;
    i++;
  }

  return counts;
}

/** Trim or extend weekly TSS arrays when phase week counts change. */
export function resizeWeeklyTssForPhaseCounts(
  phaseWeekCounts: Record<string, number>,
  weeklyTSS: Record<string, number[]>,
): Record<string, number[]> {
  const next: Record<string, number[]> = {};
  for (const ph of ATP_PHASES) {
    const newLen = Math.max(0, phaseWeekCounts[ph.key] ?? 0);
    const defaults = DEFAULT_PHASE_WEEKS[ph.key] ?? [];
    const arr = [...(weeklyTSS[ph.key] ?? defaults)];
    while (arr.length < newLen) {
      arr.push(arr[arr.length - 1] ?? defaults[arr.length] ?? 500);
    }
    next[ph.key] = arr.slice(0, newLen);
  }
  return next;
}

/** Stable key for plan start + season end + A-race + week target (wizard date-edit detection). */
export function planWeeksSyncKey(
  planStartISO: string,
  seasonEndISO: string | null | undefined,
  aRaceDateISO: string | null | undefined,
  requiredPlanWeeks: number | null,
): string {
  return `${planStartISO}|${seasonEndISO ?? ''}|${aRaceDateISO ?? ''}|${requiredPlanWeeks ?? ''}`;
}

/** Redistribute phase week counts when weeks-to-A-race changes after a date edit. */
export function redistributePlanWeeks(
  requiredPlanWeeks: number,
  prevWeeklyTSS: Record<string, number[]>,
  projectedWeeklyTss?: number,
): { phaseWeekCounts: Record<string, number>; weeklyTSS: Record<string, number[]> } {
  const phaseWeekCounts = distributePhaseWeekCounts(requiredPlanWeeks);
  let weeklyTSS = resizeWeeklyTssForPhaseCounts(phaseWeekCounts, prevWeeklyTSS);
  if (projectedWeeklyTss != null && projectedWeeklyTss > 0) {
    const { avgTSS } = computeTotals(phaseWeekCounts, weeklyTSS);
    if (avgTSS > 0) {
      weeklyTSS = scalePhaseWeeklyTss(phaseWeekCounts, weeklyTSS, projectedWeeklyTss / avgTSS);
    }
  }
  return { phaseWeekCounts, weeklyTSS };
}

/** Group persisted weeks by phase_key for program phases tab. */
export function groupWeeksByPhase(weeks: ATPWeek[]): Record<string, ATPWeek[]> {
  const map: Record<string, ATPWeek[]> = {};
  for (const w of [...weeks].sort((a, b) => a.start_day.localeCompare(b.start_day))) {
    const key = parseWeekDetails(w.details).phase_key ?? 'base';
    if (!map[key]) map[key] = [];
    map[key].push(w);
  }
  return map;
}

/** ISO start date for a week row — persisted `start_day` or computed from plan start. */
export function weekStartISOForPhaseWeek(
  phaseKey: string,
  weekIndexInPhase: number,
  phaseWeekCounts: Record<string, number>,
  planStartISO: string,
  atpWeek?: ATPWeek | null,
): string {
  if (atpWeek?.start_day) {
    return atpWeek.start_day.trim().slice(0, 10);
  }
  let weekOffset = 0;
  for (const ph of ATP_PHASES) {
    const count = Math.max(0, phaseWeekCounts[ph.key] ?? 0);
    if (ph.key === phaseKey) {
      return addDaysISO(planStartISO, (weekOffset + weekIndexInPhase) * 7);
    }
    weekOffset += count;
  }
  return planStartISO;
}

/** Find current week index (0-based) based on today's date. */
export function currentWeekIndex(weeks: ATPWeek[], today = todayISO()): number {
  for (let i = 0; i < weeks.length; i++) {
    const start = weeks[i].start_day.slice(0, 10);
    const end = addDaysISO(start, 6);
    if (today >= start && today <= end) return i;
  }
  return -1;
}

export function currentPhaseInfo(weeks: ATPWeek[], today = todayISO()): {
  phaseKey: string;
  phaseLabel: string;
  weekInPhase: number;
  phaseTotalWeeks: number;
  progressPct: number;
} | null {
  if (!weeks.length) return null;
  const idx = currentWeekIndex(weeks, today);
  if (idx < 0) return null;
  const w = weeks[idx];
  const details = parseWeekDetails(w.details);
  const phaseKey = details.phase_key ?? 'base';
  const phaseWeeks = weeks.filter(
    ww => (parseWeekDetails(ww.details).phase_key ?? 'base') === phaseKey,
  );
  const weekInPhase = phaseWeeks.findIndex(ww => ww.id === w.id) + 1;
  const phaseTotalWeeks = phaseWeeks.length;
  const progressPct = phaseTotalWeeks
    ? Math.min(100, Math.max(0, Math.round((daysBetweenISO(phaseWeeks[0].start_day.slice(0, 10), today) / (phaseTotalWeeks * 7)) * 100)))
    : 0;
  const phaseLabel = ATP_PHASES.find(p => p.key === phaseKey)?.label ?? w.period;

  return { phaseKey, phaseLabel, weekInPhase, phaseTotalWeeks, progressPct };
}

type CalendarDayBucket = { day?: string; events?: CalendarEvent[] };

function sportTypeIdFromPayload(raw: CalendarEvent['sport_type_id']): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object' && 'id' in raw) {
    const id = Number((raw as { id?: unknown }).id);
    return Number.isFinite(id) ? id : undefined;
  }
  return undefined;
}

/** Normalize API event payloads (EventResource, nested sport_type, etc.). */
export function normalizeCalendarEvent(raw: unknown): CalendarEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name = typeof o.name === 'string' ? o.name : '';
  const eventDate = o.event_date != null ? String(o.event_date) : '';
  if (!name || !eventDate) return null;
  return {
    id,
    name,
    event_date: eventDate,
    atp_priority: o.atp_priority != null ? Number(o.atp_priority) : null,
    sport_type_id: sportTypeIdFromPayload(o.sport_type_id as CalendarEvent['sport_type_id']),
    description: typeof o.description === 'string' ? o.description : undefined,
  } as CalendarEvent;
}

function extractCalendarDayBuckets(calendarResponse: unknown): CalendarDayBucket[] {
  const root = calendarResponse as { data?: unknown } | null | undefined;
  const payload = root?.data;
  if (Array.isArray(payload)) return payload as CalendarDayBucket[];
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: CalendarDayBucket[] }).data;
  }
  return [];
}

/**
 * Flat event list from GET /event/user/{id} (axios body) with optional calendar fallback.
 */
export function extractSeasonEvents(
  userEventsBody: { data?: unknown } | null | undefined,
  calendarBody?: { data?: unknown } | null,
): CalendarEvent[] {
  const payload = userEventsBody?.data;
  const seen = new Set<number>();
  const out: CalendarEvent[] = [];

  const push = (raw: unknown) => {
    const ev = normalizeCalendarEvent(raw);
    if (!ev || seen.has(ev.id)) return;
    seen.add(ev.id);
    out.push(ev);
  };

  if (Array.isArray(payload)) {
    for (const item of payload) push(item);
  } else if (payload && typeof payload === 'object') {
    const events = (payload as { events?: unknown }).events;
    if (Array.isArray(events)) {
      for (const item of events) push(item);
    }
  }

  if (out.length === 0 && calendarBody != null) {
    for (const day of extractCalendarDayBuckets(calendarBody)) {
      for (const ev of day.events ?? []) push(ev);
    }
  }

  return out;
}

/** CTL at the start of the target day; no race-day load or weeks after the event. */
export function projectCtlAtDate(ctlStart: number, weeklySeries: number[], startISO: string, targetISO: string): number {
  const days = Math.max(0, Math.min(daysBetweenISO(startISO, targetISO), weeklySeries.length * 7));
  let ctl = ctlStart;
  for (let day = 0; day < days; day++) ctl = applyDailyCtl(ctl, Math.max(0, weeklySeries[Math.floor(day / 7)] ?? 0) / 7);
  return Math.round(ctl);
}

/** Initialize new suggestions once; revisiting review preserves all manual loads. */
export function prepareReviewTss(counts: Record<string, number>, values: Record<string, number[]>, suggestedAverage: number, initialize: boolean): Record<string, number[]> {
  const snapshot = Object.fromEntries(Object.entries(values).map(([key, weeks]) => [key, [...weeks]]));
  const { avgTSS } = computeTotals(counts, snapshot);
  return initialize && suggestedAverage > 0 && avgTSS > 0
    ? scalePhaseWeeklyTss(counts, snapshot, suggestedAverage / avgTSS)
    : snapshot;
}
